import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, ArrowRight, Globe, Shield } from "lucide-react";

export default function SuperAdminLogin() {
    const { user, checking, superAdminLogin } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (!checking && user) {
        if (user.role === "super_admin") return <Navigate to="/companies" replace />;
        return <Navigate to="/dashboard" replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await superAdminLogin(email.trim(), password);
        setLoading(false);
        if (res.ok) {
            navigate("/companies", { replace: true });
        } else {
            setError(res.error || "Login failed");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: "linear-gradient(135deg, #0F1A2E 0%, #1F1F1F 100%)" }} data-testid="super-admin-login-page">
            <div className="w-full max-w-md bg-white border border-ink/10 rounded-lg shadow-lift overflow-hidden">
                <div className="px-8 py-7 border-b border-ink/5" style={{ background: "linear-gradient(135deg, #0F1A2E 0%, #1F1F1F 100%)" }}>
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 rounded-md bg-white/10 backdrop-blur flex items-center justify-center">
                            <Shield className="w-5 h-5 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Restricted</div>
                            <div className="font-display font-bold text-lg">Platform Super Admin</div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="p-8" data-testid="super-admin-form">
                    <p className="text-sm text-ink/60 mb-6 leading-relaxed">
                        Sign in to manage all tenants on your HRM platform — suspend or reactivate companies, audit data, view platform-wide stats.
                    </p>

                    <div className="space-y-6">
                        <div>
                            <label className="overline">Super-admin email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="super-admin-email-input" className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink" placeholder="superadmin@yourdomain.com" />
                        </div>
                        <div>
                            <label className="overline">Password</label>
                            <div className="relative">
                                <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="super-admin-password-input" className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 pr-10 px-0 focus:border-klein focus:outline-none text-ink" placeholder="••••••••" />
                                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink" tabIndex={-1}>
                                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="mt-5 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive rounded-md" data-testid="super-admin-error">
                            {error}
                        </div>
                    )}

                    <button type="submit" disabled={loading} data-testid="super-admin-submit" className="mt-7 group w-full bg-ink text-white px-6 py-3.5 font-medium flex items-center justify-between hover:bg-ink/90 disabled:opacity-60 transition-colors rounded-md">
                        <span>{loading ? "Authenticating…" : "Sign in to platform"}</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>

                    <div className="mt-5 text-center">
                        <Link to="/super-admin/forgot-password" className="text-xs uppercase tracking-[0.15em] text-ink/50 hover:text-klein" data-testid="super-admin-forgot-link">
                            Forgot password?
                        </Link>
                    </div>

                    <div className="mt-5 pt-6 border-t border-ink/10 text-center">
                        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink/50 hover:text-klein" data-testid="back-to-company-login">
                            <Globe className="w-3 h-3" /> Back to company login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
